/**
 * Display-side grouping and stream classification for the Activity timeline.
 *
 * Deliberately dependency-free (no React, no API client, no design system) so
 * the rules can be exercised directly. These are the two decisions that change
 * what an operator sees, and both are easy to get subtly wrong: collapsing rows
 * that are not actually repeats, or classifying a person's action as machine
 * activity and hiding it from the default view.
 */

/** The subset of an activity row these rules read. */
export interface GroupableActivityRow {
  id: string;
  createdAt: string;
  action: string;
  entityType: string;
  entityId?: string;
  outcome?: string;
  summary?: string;
  notes?: string;
  actorType?: string;
  source?: string;
}

export interface ActivityGroup<T extends GroupableActivityRow = GroupableActivityRow> {
  key: string;
  /** Most recent row in the group — rows arrive newest first. */
  row: T;
  rows: T[];
  firstSeen: string;
  lastSeen: string;
}

/**
 * A row nobody typed: the scheduler, a webhook, a provider, or the AI.
 *
 * Mirrors the server-side `streamCondition` predicate. A null `actor_type` is
 * User activity — every such row in production predates the column and is a
 * browser-initiated edit — unless the row came from a cron run.
 */
export function isSystemRow(row: GroupableActivityRow): boolean {
  if (row.source === 'cron') return true;
  return row.actorType != null && row.actorType !== 'user';
}

/**
 * Collapse consecutive identical System events into one row.
 *
 * The history is not deleted or hidden: every raw row is retained inside the
 * group and listed in the drawer. This only stops 147 byte-identical
 * "Airtable mirror failed" rows from being 147 lines of a timeline that is
 * supposed to show what happened.
 *
 * Only *consecutive* rows collapse, and only machine-authored ones — two
 * separate incidents with a real event between them stay separate, and a
 * person doing the same thing twice is never merged.
 */
export function groupRepeats<T extends GroupableActivityRow>(rows: T[]): Array<ActivityGroup<T>> {
  const groups: Array<ActivityGroup<T>> = [];
  for (const row of rows) {
    const previous = groups[groups.length - 1];
    if (
      previous
      && isSystemRow(row)
      && isSystemRow(previous.row)
      && repeatKey(row) === repeatKey(previous.row)
    ) {
      previous.rows.push(row);
      // Rows are newest-first, so each additional row is older.
      previous.firstSeen = row.createdAt;
      continue;
    }
    groups.push({ key: row.id, row, rows: [row], firstSeen: row.createdAt, lastSeen: row.createdAt });
  }
  return groups;
}

/**
 * What makes two rows "the same event again".
 *
 * The summary and notes are included because they carry the subject name and
 * the error: two mirror failures for different Users, or the same User failing
 * for a different reason, are different events and must not merge.
 */
function repeatKey(row: GroupableActivityRow): string {
  return [
    row.action,
    row.entityType,
    row.entityId ?? '',
    row.outcome ?? '',
    row.summary ?? '',
    row.notes ?? '',
  ].join('|');
}
